import { Flex, Padding } from 'components/base/layout-components';
import React, { useEffect, useState } from 'react';
import GitIntegrationTitle from './GitIntegrationTitle';
import ToggleBox from 'components/ToggleBox';
import { SPACES } from 'common/constants/sizes';
import styled from 'styled-components';
import { getColor } from 'common/constants/_colors';
import Table from 'components/base/Table';
import { useRepositoriesApi } from 'common/fetchHooks/useRepositoriesApi';
import TableSearch from 'components/base/Table/TableSearch';
import { PAGE_SIZE } from './integrations.service';
import { CircularProgress } from '@material-ui/core';
import {
  RATE_LIMIT_ISSUE_THRESHOLD,
  calculateRateLimitCount
} from 'common/hooks/useSensorAuthRateLimitCounts';

const TableWrapper = styled.div`
  padding: ${SPACES['medium']};
  padding-bottom: 1rem;

  .MuiTableHead-root > .MuiTableRow-head:first-child {
    border-top: 0;
  }

  .MuiTableHead-root .MuiTableCell-head:last-child {
    width: 30%;
  }

  .MuiTableBody-root > .MuiTableRow-root:last-child > .MuiTableCell-body {
    border-bottom: 0;
  }

  .MuiTableCell-root {
    border: 0;
    border-bottom: 1px solid ${getColor('catskillWhite')};
    padding: 1rem;
    height: unset !important;
  }
`;

const DisabledTableWrapper = styled.div`
  ${({ disabled }) =>
    disabled
      ? `
  background: white;
  opacity: 0.3;
  pointer-events: none;
  `
      : ''};
`;

const TableSearchWrapper = styled.div`
  width: 20%;
`;

const Spinner = () => (
  <Flex justify='center' padding='medium' data-testid='spinner'>
    <CircularProgress size='4rem' color='primary' />
  </Flex>
);

const GitIntegration = ({
  id,
  name,
  sensor_name,
  state,
  url,
  repos,
  account,
  gitIntegrations = [],
  deleteGitIntegrationFF,
  updateSensorAuthorization,
  canAddMoreRepos,
  toggleModal,
  repositories,
  createRepositories,
  setIsProcessingRepos,
  toggleSnackBar,
  isTeamLicense,
  getSensorAuthorizations,
  getRepositories,
  deleteSensorAuthorization,
  getTableData,
  tableConfig,
  is_on_prem,
  showGitStreamIntegration,
  showGitstreamBitbucketIntegration,
  showGitstreamGitlabServerIntegration,
  showGitstreamGithubServerIntegration,
  prStats,
  autoRepoDiscoveryConfig,
  isMultiTokenEnabled,
  profile = {},
  readonly,
  selectionProps,
  fetchRepositoriesExtraParams
}) => {
  const stateOK = state === 'ok';
  const isSelectedOnlyMode = !!selectionProps?.isSelectedOnly;
  const isRateLimitThresholdExceeded =
    calculateRateLimitCount({ profile }) > RATE_LIMIT_ISSUE_THRESHOLD;

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [gitIntegrationsLength, setGitIntegrationsLength] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(gitIntegrations.length === 1 || isSelectedOnlyMode);
    setGitIntegrationsLength(gitIntegrations.length);
  }, [gitIntegrations.length, isSelectedOnlyMode]);

  const gitStreamConnectedIntegration =
    gitIntegrations?.find(({ gitstream_enabled }) => gitstream_enabled) || {};
  const currentGitIntegration = gitIntegrations?.find(
    integration => integration.id === id
  );
  const { data, totalRepos, loading, setLoading } = useRepositoriesApi({
    pageSize: PAGE_SIZE,
    sensor_authorization_id: id,
    pageIndex: page,
    search,
    isOpen,
    repositories,
    extraParams: fetchRepositoriesExtraParams
  });

  const tableDataPaginated = getTableData(data);

  const handleSetValueChange = value => {
    setPage(0);
    setSearch(value);
  };

  const handleToggleBox = () => setIsOpen(!isOpen);

  const title = (
    <GitIntegrationTitle
      sensor_name={sensor_name}
      gitIntegrations={gitIntegrations}
      updateSensorAuthorization={updateSensorAuthorization}
      id={id}
      name={name}
      stateOK={stateOK}
      tableDataLength={repos.length}
      url={url}
      deleteGitIntegrationFF={deleteGitIntegrationFF}
      account={account}
      canAddMoreRepos={canAddMoreRepos}
      toggleModal={toggleModal}
      repositories={repositories}
      createRepositories={createRepositories}
      setIsProcessingRepos={setIsProcessingRepos}
      toggleSnackBar={toggleSnackBar}
      isTeamLicense={isTeamLicense}
      getSensorAuthorizations={getSensorAuthorizations}
      getRepositories={getRepositories}
      deleteSensorAuthorization={deleteSensorAuthorization}
      is_on_prem={is_on_prem}
      showGitStreamIntegration={showGitStreamIntegration}
      showGitstreamBitbucketIntegration={showGitstreamBitbucketIntegration}
      showGitstreamGitlabServerIntegration={
        showGitstreamGitlabServerIntegration
      }
      showGitstreamGithubServerIntegration={
        showGitstreamGithubServerIntegration
      }
      gitIntegration={currentGitIntegration}
      gitStreamConnectedIntegrationId={gitStreamConnectedIntegration.id}
      prStats={prStats}
      autoRepoDiscoveryConfig={autoRepoDiscoveryConfig}
      isMultiTokenEnabled={isMultiTokenEnabled}
      isRateLimitThresholdExceeded={isRateLimitThresholdExceeded}
      readonly={readonly}
    />
  );

  const TableComponent = () => (
    <TableWrapper>
      <Table
        columns={tableConfig}
        data={tableDataPaginated}
        withPagination
        totalRows={totalRepos}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[]}
        onChangePage={(event, nextPage) => {
          setLoading(true);
          setPage(nextPage);
        }}
        page={page}
        isPaginationPositionBottom
      />
    </TableWrapper>
  );

  return (
    <Padding b='small' key={id} data-cy='integration-row'>
      <ToggleBox
        isControlled
        collapsed={!isOpen}
        notActive={!stateOK}
        title={title}
        onClick={handleToggleBox}
        isShowSingleToggleOpen={gitIntegrationsLength === 1}
      >
        <Padding padding='small'>
          {loading && data?.length === 0 ? (
            <Spinner />
          ) : (
            <DisabledTableWrapper disabled={loading}>
              <TableSearchWrapper>
                <TableSearch
                  enabled
                  value={search}
                  placeholder='Search repositories'
                  setValue={search => handleSetValueChange(search)}
                />
              </TableSearchWrapper>
              {(search && data?.length === 0) ||
              (!loading && data?.length === 0 && !search) ? (
                <div />
              ) : (
                <TableComponent />
              )}
            </DisabledTableWrapper>
          )}
        </Padding>
      </ToggleBox>
    </Padding>
  );
};

export default GitIntegration;
